import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AppStateService } from '@shared/services/app-state.service';
import { CandidateDocumentType } from '@pages/candidates/classes/CandidateDocumentType';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { DocumentTypeNamePipe } from '@shared/modules/key-value-detail/pipes/document-type-name.pipe';
import { CandidatesService } from '@pages/candidates/services/candidates.service';
import { keys } from 'lodash-es';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppConstants } from '@config/app.constant';
import { isNonFalsyValue } from '@shared/utils/check-falsy-value.util';
import { MatModalBaseComponent } from '@shared/modules/mat-modal/mat-modal-base.component';
import { DocumentUploadModalData } from '@shared/modules/modals/candidate-modals/document-upload-modal/classes/DocumentUploadModalConfig';
import { CandidateStateService } from '@pages/candidates/services/base/candidate-state.service';

@UntilDestroy()
@Component({
  selector: 'app-document-upload-modal',
  templateUrl: './document-upload-modal.component.html',
  styleUrls: ['./document-upload-modal.component.scss'],
  providers: [DocumentTypeNamePipe],
})
export class DocumentUploadModalComponent
  extends MatModalBaseComponent<DocumentUploadModalData>
  implements OnInit, OnDestroy {
  types$: Observable<CandidateDocumentType[]>;
  types: CandidateDocumentType[] = [];
  isExpiryDateFieldShown = true;
  isPositionSelectionFieldShown = false;
  documentTypesWithExpiryDate = AppConstants.documentTypesWithExpiryDate;
  today = new Date();
  documentTypeSelectedHint = '';

  form: FormGroup;
  candidateId: number;

  constructor(
    private appService: AppStateService,
    private documentNamePipe: DocumentTypeNamePipe,
    private formBuilder: FormBuilder,
    private candidateState: CandidateStateService,
    private candidateService: CandidatesService,
    protected injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.form = this.initForm();
    this.primaryButtonDisabled = true;
    this.candidateId = this.data.candidateId;

    this.types$ = this.appService.getDocumentTypes().pipe(
      tap((types: CandidateDocumentType[]) => {
        this.types = types;
      }),
      map((types) => {
        return types.map((type) => {
          return {
            ...type,
            name: this.translateService.instant(
              this.documentNamePipe.transform(type.name)
            ) as string,
          };
        });
      })
    );

    this.listenFormChanges();
  }

  private initForm(): FormGroup {
    return this.formBuilder.group({
      documentTypeId: [null, [Validators.required]],
      file: [null, [Validators.required]],
      expiryDate: [null, [Validators.required]],
    });
  }

  private listenFormChanges(): void {
    this.form
      .get('documentTypeId')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        const documentId = this.form.get('documentTypeId').value as number;
        const documentType = this.types.find((document) => document.id === documentId);

        if (documentType) {
          this.handleDocumentWithExpiryDate(documentType);
          this.handleDocumentWithCVType(documentType);
          this.handleDocumentWithPositionSelection(documentType);
        }
      });

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.primaryButtonDisabled = this.form.invalid;
    });
  }

  private handleDocumentWithPositionSelection(documentType: CandidateDocumentType): void {
    this.form.removeControl('positionId');
    this.isPositionSelectionFieldShown = false;

    const isPositionRequiredTypeSelected = AppConstants.documentTypesWithPositionSelection.includes(
      documentType.name
    );

    if (isPositionRequiredTypeSelected) {
      this.form.addControl('positionId', new FormControl(null));
      this.form.get('positionId').markAsTouched();

      this.isPositionSelectionFieldShown = true;
    }
  }

  private handleDocumentWithExpiryDate(documentType: CandidateDocumentType): void {
    this.isExpiryDateFieldShown = this.documentTypesWithExpiryDate.indexOf(documentType.name) > -1;

    const expiryDateControl = this.form.get('expiryDate');
    expiryDateControl.setValidators(this.isExpiryDateFieldShown ? [Validators.required] : []);
    expiryDateControl.setValue(null);
    expiryDateControl.updateValueAndValidity();
  }

  private handleDocumentWithCVType(documentType: CandidateDocumentType): void {
    if (documentType?.name !== AppConstants.cvDocumentKey) {
      this.documentTypeSelectedHint = '';
      return;
    }

    this.documentTypeSelectedHint = 'candidates.cv_parse_hint';
  }

  uploadDocument() {
    this.primaryButtonDisabled = true;

    const formData = new FormData();
    const controls = keys(this.form.getRawValue());
    controls.forEach((controlName: string) => {
      const value = this.form.get(controlName).value as string | Blob;

      if (isNonFalsyValue(value).allowFile().check()) {
        formData.append(controlName, value);
      }
    });

    this.candidateService
      .callUploadDocument(this.candidateId, formData)
      .subscribe((result) => this.handleConfirmActionResponse(result));
  }

  ngOnDestroy() {
    this.candidateState.setState({
      candidatePositionsForDocumentUploadLoaded: false,
      candidatePositionsForDocumentUploadLoading: false,
      candidatePositionsForDocumentUpload: [],
    });

    return super.ngOnDestroy();
  }
}
